/**
 * "What happened to this item?" — one plain outcome line per archived or
 * duplicate item, read from the database's own records (item_outcomes, mig 860:
 * closures, lineage, archive transitions, duplicate_of_id). Read only.
 *
 * The outcome words are the owner's, approved 2026-10-14 (mig 869): Merged into,
 * Split into, Replaced by, Duplicate of, Retired, Archived. A reframe is not an
 * outcome and has no word here.
 */

import { getTypedSupabaseClient } from '../typedSupabaseClient';
import type { ItemOutcomeWord } from '../../../supabase/functions/_shared/itemOutcomeLine.ts';

export {
  NO_OUTCOME_RECORD,
  outcomeByline,
  outcomeHeadline,
  outcomeLead,
} from '../../../supabase/functions/_shared/itemOutcomeLine.ts';
export type { ItemOutcomeWord };

export interface ItemOutcome {
  itemId: string;
  outcome: ItemOutcomeWord;
  /** When it happened; null when the item is archived but no record says when. */
  at: Date | null;
  /** Who did it (user id, or a non-id like 'system'); null when unrecorded. */
  actorId: string | null;
  reason: string | null;
  pointerItemId: string | null;
  pointerItemTitle: string | null;
  /** For "split into": how many more items besides the pointer. */
  alsoCount: number;
  /** True only when restore_retired_item will accept this person's take-back. */
  youMayRestore: boolean;
}

export async function fetchItemOutcomes(itemIds: readonly string[]): Promise<Map<string, ItemOutcome>> {
  const out = new Map<string, ItemOutcome>();
  const ids = Array.from(new Set(itemIds.filter(Boolean)));
  if (ids.length === 0) return out;
  const { data, error } = await getTypedSupabaseClient().rpc('item_outcomes', { p_item_ids: ids });
  if (error) throw new Error(error.message);
  for (const row of data ?? []) {
    out.set(row.item_id, {
      itemId: row.item_id,
      outcome: row.outcome as ItemOutcomeWord,
      at: row.outcome_at ? new Date(row.outcome_at) : null,
      actorId: row.actor_id ?? null,
      reason: row.reason ?? null,
      pointerItemId: row.pointer_item_id ?? null,
      pointerItemTitle: row.pointer_item_title ?? null,
      alsoCount: row.also_count ?? 0,
      youMayRestore: row.you_may_restore === true,
    });
  }
  return out;
}
